import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, throwError } from 'rxjs';
import { Duration } from 'ts-luxon';
import { v4 as uuidv4 } from 'uuid';

import { DeviceAuthenticationStatusService } from '@shure/shared/angular/data-access/device-authentication';
import {
	DeviceResetScope,
	NodeChangeType,
	AudioChannelFeatureUpdateInput,
	DeviceFeatureUpdateInput,
	ApolloQueryErrorMapper,
	ApolloQueryError,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	waitForGQLCompleteQueryOrThrow
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapMicOptimizationModeToSysApi,
	mapNoiseDetectionSensitivityToSysApi,
	mapNoiseRemovalLevelToSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import {
	ResponseState,
	ChannelGroup,
	Response,
	DirectOutputTapPoint,
	AutomixMicOptimizationMode,
	AutomixNoiseDetectionSensitivity,
	AutomixNoiseRemovalLevel,
	DeviceConferenceOperationMode
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceReadonlyStateProvider } from '@shure/shared/angular/utils/device-readonly';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { cachedObservableById, mutationThrottle, noMoreSubscribers, tapOnce } from '@shure/shared/typescript/rxjs';

import { IntellimixApiService } from '../api/intellimix-api.service';
import {
	IntellimixDevice,
	IntellimixAutomixChannel,
	IntellimixBasicAutomixChannel,
	IntellimixInputChannel,
	ChannelAutomixMode
} from '../api/models';

import {
	IntellimixAutomixChannelQueryGQL,
	IntellimixAutomixChannelSubscriptionGQL,
	IntellimixBasicAutomixChannelQueryGQL,
	IntellimixBasicAutomixChannelSubscriptionGQL,
	IntellimixChannelMutationGQL,
	IntellimixDeviceMutationGQL,
	IntellimixDeviceAutomixGainMeteringMutationGQL,
	IntellimixDeviceQueryGQL,
	IntellimixDeviceSubscriptionGQL,
	IntellimixEqualizerSubscriptionGQL,
	IntellimixInputChannelQueryGQL,
	IntellimixInputChannelSubscriptionGQL,
	ResetAutomixerDefaultsMutationGQL,
	UpdateAutomixerSourceSelectorGQL
} from './graphql/generated/intellimix.generated';
import {
	mapIntellimixDeviceFromSysApi,
	mapAutomixChannelFromSysApi,
	mapBasicAutomixChannelFromSysApi,
	mapInputChannelFromSysApi
} from './mappers';

@Injectable()
export class SysApiIntellimixApiService implements IntellimixApiService {
	private readonly logger: ILogger;

	private readonly intellimixDeviceSubscriptionManager: CombiningSubscriptionManager;
	private readonly intellimixBasicAutomixChannelSubscriptionManager: CombiningSubscriptionManager;
	private readonly intellimixAutomixChannelSubscriptionManager: CombiningSubscriptionManager;
	private readonly intellimixInputChannelSubscriptionManager: CombiningSubscriptionManager;
	private readonly intellimixEqualizerSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly intellimixDeviceQueryGQL: IntellimixDeviceQueryGQL,
		private readonly intellimixBasicAutomixChannelQueryGQL: IntellimixBasicAutomixChannelQueryGQL,
		private readonly intellimixAutomixChannelQueryGQL: IntellimixAutomixChannelQueryGQL,
		private readonly intellimixInputChannelQueryGQL: IntellimixInputChannelQueryGQL,
		private readonly intellimixChannelMutationGQL: IntellimixChannelMutationGQL,
		private readonly intellimixDeviceMutationGQL: IntellimixDeviceMutationGQL,
		private readonly intellimixDeviceAutomixGainMeteringMutationGQL: IntellimixDeviceAutomixGainMeteringMutationGQL,
		private readonly intellimixDeviceAutomixGainMeteringMutation: IntellimixDeviceAutomixGainMeteringMutationGQL,
		private readonly resetAutomixerDefaultsMutationGQL: ResetAutomixerDefaultsMutationGQL,
		private readonly updateAutomixerSourceSelectorGQL: UpdateAutomixerSourceSelectorGQL,
		intellimixDeviceSubscriptionGQL: IntellimixDeviceSubscriptionGQL,
		intellimixInputChannelSubscriptionGQL: IntellimixInputChannelSubscriptionGQL,
		intellimixBasicAutomixChannelSubscriptionGQL: IntellimixBasicAutomixChannelSubscriptionGQL,
		intellimixAutomixChannelSubscriptionGQL: IntellimixAutomixChannelSubscriptionGQL,
		intellimixEqualizerSubscriptionGQL: IntellimixEqualizerSubscriptionGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly readonlyStateProvider: DeviceReadonlyStateProvider,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiIntellimixService');

		this.intellimixDeviceSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiIntellimixApiService:Device',
				changeTypes: [
					NodeChangeType.DeviceAutomixGainMetering,
					NodeChangeType.DeviceAllIntellimixBypass,
					NodeChangeType.DeviceAuthentication,
					NodeChangeType.DeviceCoverageMode,
					NodeChangeType.DeviceAllEqualizersBypass,
					NodeChangeType.DeviceDanteAudioNetworkAccess,
					NodeChangeType.DeviceLicense,
					NodeChangeType.DeviceAudioChannelCount,
					NodeChangeType.DeviceVirtualAcousticBoundary,
					NodeChangeType.DeviceConferencingOperationMode,
					NodeChangeType.DeviceDectRfSpectrumScan
				],
				subscriptionGql: intellimixDeviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.intellimixBasicAutomixChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiIntellimixApiService:BasicAutomix',
				changeTypes: [
					NodeChangeType.AudioChannelName,
					NodeChangeType.AudioChannelDanteEncryptionStatus,
					NodeChangeType.AudioChannelPostGateMute
				],
				subscriptionGql: intellimixBasicAutomixChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.intellimixAutomixChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiIntellimixApiService:Automix',
				changeTypes: [
					NodeChangeType.AudioChannelName,
					NodeChangeType.AudioChannelMute,
					NodeChangeType.AudioChannelPostGateMute,
					NodeChangeType.AudioChannelGain,
					NodeChangeType.AudioChannelPostGateGain,
					NodeChangeType.AudioChannelDanteEncryptionStatus,
					NodeChangeType.AudioChannelAutomixer,
					NodeChangeType.AudioChannelCompressor,
					NodeChangeType.AudioChannelDelay,
					NodeChangeType.AudioChannelAutomaticGainControl,
					NodeChangeType.AudioChannelAcousticEchoCancellation,
					NodeChangeType.AudioChannelNoiseReduction,
					NodeChangeType.AudioChannelDenoiser,
					NodeChangeType.AudioChannelDeverb,
					NodeChangeType.AudioChannelAutomixSourceSelector,
					NodeChangeType.AudioChannelMicPolarPattern,
					NodeChangeType.AudioChannelLobeConfiguration,
					NodeChangeType.AudioChannelMicOptimization,
					NodeChangeType.AudioChannelEqualizer,
					NodeChangeType.AudioChannelVoiceActivityDetectionSensitivity,
					NodeChangeType.AudioChannelVoiceActivityDetectionNoiseReduction,
					NodeChangeType.AudioChannelAutomixerChannelActive
				],
				subscriptionGql: intellimixAutomixChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.intellimixInputChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiIntellimixApiService:Input',
				changeTypes: [
					NodeChangeType.AudioChannelName,
					NodeChangeType.AudioChannelPostGateSolo,
					NodeChangeType.AudioChannelAutomixerInputAlwaysOn,
					NodeChangeType.AudioChannelAutomixerInputPriority,
					NodeChangeType.AudioChannelAutomixerInputSendToMonoMix,
					NodeChangeType.AudioChannelDanteEncryptionStatus,
					NodeChangeType.AudioChannelAutomaticGainControl,
					NodeChangeType.AudioChannelDirectOutputTapPoint,
					NodeChangeType.AudioChannelPostGateGain,
					NodeChangeType.AudioChannelPostGateMute,
					NodeChangeType.AudioChannelLobeConfiguration,
					NodeChangeType.AudioChannelMicPolarPattern,
					NodeChangeType.AudioChannelAnalogInputUse,
					NodeChangeType.AudioChannelAutomixerChannelActive
				],
				subscriptionGql: intellimixInputChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.intellimixEqualizerSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'SysApiIntellimixApiService:Eq',
				changeTypes: [NodeChangeType.EqualizerFilterConfiguration],
				subscriptionGql: intellimixEqualizerSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	@cachedObservableById()
	public getDevice$(deviceId: string): Observable<IntellimixDevice> {
		this.logger.trace('getDevice$()', 'Querying intellimix device', { deviceId });
		const queryId = uuidv4();
		return combineLatest([
			this.intellimixDeviceQueryGQL
				.watch({ variables: { deviceId }, errorPolicy: 'ignore' })
				.valueChanges.pipe(waitForGQLCompleteQueryOrThrow()),
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId),
			this.readonlyStateProvider.isReadonly$(deviceId)
		]).pipe(
			map(([query, auth, isReadonly]) => {
				if (query.data?.node && 'description' in query.data.node) {
					const intellimixDevice = mapIntellimixDeviceFromSysApi(
						query.data.node as Parameters<typeof mapIntellimixDeviceFromSysApi>[0],
						auth.authenticated,
						isReadonly
					);
					this.logger.trace('getDevice$()', 'Query updated', intellimixDevice);
					return intellimixDevice;
				}

				this.logger.error('getDevice$()', 'Failed to query Intellimix device', { deviceId, queryId });
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tapOnce((device) => {
				const automixChannel = device.features.channels?.channels.find(
					(channel) => channel.group === ChannelGroup.Automix
				);
				if (automixChannel && automixChannel.id) {
					this.intellimixDeviceSubscriptionManager.subscribe(queryId, deviceId);
				}
			}),
			noMoreSubscribers(() => {
				this.logger.trace('intellimixDeviceQueryGQL.onSubscribers.noMoreSubscribers', '', {
					deviceId,
					queryId
				});
				this.intellimixDeviceSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error: Error) => {
				this.logger.error('getDevice$()', 'Failed to query device', {
					id: deviceId,
					error
				});
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	@cachedObservableById()
	public getIntellimixAutomixChannel$(channelId: string): Observable<IntellimixAutomixChannel> {
		const queryId = uuidv4();
		const eqQueryId = uuidv4();
		this.logger.trace('getIntellimixAutomixChannel$()', 'Querying intellimix automix channel', {
			channelId,
			queryId
		});
		return this.intellimixAutomixChannelQueryGQL
			.watch({ variables: { channelId }, errorPolicy: 'ignore' })
			.valueChanges.pipe(
				waitForGQLCompleteQueryOrThrow(),
				map((query) => {
					if (query.data?.node && 'description' in query.data.node) {
						const automixChannel = mapAutomixChannelFromSysApi(
							query.data.node as Parameters<typeof mapAutomixChannelFromSysApi>[0]
						);
						this.logger.trace('getIntellimixAutomixChannel$()', 'Query updated', { channelId, queryId });
						return automixChannel;
					}

					this.logger.error('getIntellimixAutomixChannel$()', 'Failed to query Intellimix automix channel', {
						channelId
					});
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tapOnce((channel) => {
					this.intellimixAutomixChannelSubscriptionManager.subscribe(queryId, channel.id);

					this.intellimixEqualizerSubscriptionManager.subscribeToOnly(
						eqQueryId,
						channel.features.peq.ids || []
					);
				}),
				noMoreSubscribers(() => {
					this.logger.trace(
						'intellimixAutomixChannelQueryGQL.onSubscribers.noMoreSubscribers',
						'intellimixAutomixChannelSubscriptionManager',
						{
							channelId,
							queryId
						}
					);
					this.intellimixAutomixChannelSubscriptionManager.unsubscribeQuery(queryId);

					this.intellimixEqualizerSubscriptionManager.unsubscribeQuery(eqQueryId);
					this.logger.trace(
						'intellimixAutomixChannelQueryGQL.onSubscribers.noMoreSubscribers',
						'intellimixEqualizerSubscriptionManager',
						{
							channelId,
							eqQueryId
						}
					);
				}),
				catchError((error: Error) => {
					this.logger.error('getIntellimixAutomixChannel$()', 'Failed to query intellimix automix channel', {
						id: channelId,
						error
					});
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	@cachedObservableById()
	public getIntellimixBasicAutomixChannel$(channelId: string): Observable<IntellimixBasicAutomixChannel> {
		const queryId = uuidv4();
		this.logger.trace('getIntellimixBasicAutomixChannel$()', 'Querying intellimix basic automix channel', {
			channelId,
			queryId
		});
		return this.intellimixBasicAutomixChannelQueryGQL
			.watch({ variables: { channelId }, errorPolicy: 'ignore' })
			.valueChanges.pipe(
				waitForGQLCompleteQueryOrThrow(),
				map((query) => {
					if (query.data?.node && 'description' in query.data.node) {
						const automixChannel = mapBasicAutomixChannelFromSysApi(
							query.data.node as Parameters<typeof mapBasicAutomixChannelFromSysApi>[0]
						);
						this.logger.trace('getIntellimixBasicAutomixChannel$()', 'Query updated', {
							channelId,
							queryId
						});
						return automixChannel;
					}

					this.logger.error(
						'getIntellimixBasicAutomixChannel$()',
						'Failed to query Intellimix automix channel',
						{
							channelId
						}
					);
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tapOnce((channel) =>
					this.intellimixBasicAutomixChannelSubscriptionManager.subscribe(queryId, channel.id)
				),
				noMoreSubscribers(() => {
					this.logger.trace('intellimixBasicAutomixChannelQueryGQL.onSubscribers.noMoreSubscribers', '', {
						channelId,
						queryId
					});
					this.intellimixBasicAutomixChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error: Error) => {
					this.logger.error(
						'getIntellimixBasicAutomixChannel$()',
						'Failed to query intellimix automix channel',
						{
							id: channelId,
							error
						}
					);
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	@cachedObservableById()
	public getIntellimixInputChannel$(channelId: string): Observable<IntellimixInputChannel> {
		const queryId = uuidv4();
		this.logger.trace('getIntellimixInputChannel$()', 'Querying intellimix input channel', { channelId, queryId });
		return this.intellimixInputChannelQueryGQL
			.watch({ variables: { channelId }, errorPolicy: 'all' })
			.valueChanges.pipe(
				waitForGQLCompleteQueryOrThrow(),
				map((query) => {
					if (query.data?.node && 'description' in query.data.node) {
						const inputChannel = mapInputChannelFromSysApi(
							query.data.node as Parameters<typeof mapInputChannelFromSysApi>[0]
						);
						this.logger.trace('getIntellimixInputChannel$()', 'Query updated', { channelId, query });
						return inputChannel;
					}

					this.logger.error('getIntellimixInputChannel$()', 'Failed to query Intellimix input channel', {
						channelId
					});
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tapOnce((channel) => this.intellimixInputChannelSubscriptionManager.subscribe(queryId, channel.id)),
				noMoreSubscribers(() => {
					this.logger.trace('intellimixInputChannelSubscriptionManager.onSubscribers.noMoreSubscribers', '', {
						channelId,
						queryId
					});
					this.intellimixInputChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error: Error) => {
					this.logger.error('getIntellimixInputChannel$()', 'Failed to query intellimix input channel', {
						id: channelId,
						error
					});
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}
	@mutationThrottle()
	public updateChannelGain(channelId: string, gain: number): Observable<Response<void, string>> {
		this.logger.trace('gainValueChaned()', 'updating channel gain', { channelId });
		return this.updateChannel(channelId, { gain: { gain } });
	}

	@mutationThrottle()
	public updateChannelPostGateGain(channelId: string, gain: number): Observable<Response<void, string>> {
		this.logger.trace('updateChannelPostGateGain()', 'updating channel post gate gain', { channelId });
		return this.updateChannel(channelId, { postGateGain: { gain: gain } });
	}

	public updateChannelName(channelId: string, name: string): Observable<Response<void, string>> {
		this.logger.trace('updateChannelName()', 'updating channel name', { channelId });

		return this.updateChannel(channelId, { name: { name } });
	}

	public setSendToMix(channelId: string, sendToMixbusEnabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setSendToMix()', 'setting channel to send to mixbus on:', {
			channelId,
			sendToMixbusEnabled
		});
		return this.updateChannel(channelId, { automixerInputSendToMonoMix: { enabled: sendToMixbusEnabled } });
	}

	public setPriorityMode(channelId: string, priorityEnabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setPriorityMode()', 'Setting priority mode', { channelId, priorityEnabled });
		return this.updateChannel(channelId, { automixerInputPriority: { enabled: priorityEnabled } });
	}
	public setAlwaysOnMode(channelId: string, alwaysOnEnabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setAlwaysOnMode()', 'Setting always on mode', { channelId, alwaysOnEnabled });
		return this.updateChannel(channelId, { automixerInputAlwaysOn: { enabled: alwaysOnEnabled } });
	}

	public setMuteMode(channelId: string, muted: boolean): Observable<Response<void, string>> {
		this.logger.trace('setMuteMode()', 'Setting mute mode', { channelId, muted });
		return this.updateChannel(channelId, { mute: { muted } });
	}

	public setPostGateMuteMode(channelId: string, muted: boolean): Observable<Response<void, string>> {
		this.logger.trace('setPostGateMuteMode()', 'Setting post gate mute mode', { channelId, muted });
		return this.updateChannel(channelId, { postGateMute: { muted: muted } });
	}

	public setAutomixerEnabled(channelId: string, isEnabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setAutomixerEnabled()', 'Setting Automixer Enabled', { channelId, isEnabled });
		return this.updateChannel(channelId, { automixer: { enabled: isEnabled } });
	}

	public setNoiseDetectionSensitivity(
		channelId: string,
		value: AutomixNoiseDetectionSensitivity
	): Observable<Response<void, string>> {
		this.logger.trace('setNoiseDetectionSensitivity()', 'Setting noise detection sensitivity', {
			channelId,
			value
		});
		return this.updateChannel(channelId, {
			voiceActivityDetectionSensitivity: { sensitivity: mapNoiseDetectionSensitivityToSysApi(value) }
		});
	}

	public setNoiseRemovalLevel(
		channelId: string,
		value: AutomixNoiseRemovalLevel
	): Observable<Response<void, string>> {
		this.logger.trace('setNoiseRemovalLevel()', 'Setting noise removal level', { channelId, value });
		return this.updateChannel(channelId, {
			voiceActivityDetectionNoiseReduction: { level: mapNoiseRemovalLevelToSysApi(value) }
		});
	}

	public setAutomixMode(channelId: string, mode: ChannelAutomixMode): Observable<Response<void, string>> {
		this.logger.trace('setAutomixMode()', 'Setting automix mode', { channelId, mode });
		return this.updateChannel(channelId, { automixer: { mode } });
	}

	public setAutomixSoloMode(automixChannelId: string, soloChannelId?: string): Observable<Response<void, string>> {
		this.logger.trace('setAutomixSoloMode()', 'Setting automix solo mode', { automixChannelId, soloChannelId });
		return this.updateAutomixerSourceSelectorGQL
			.mutate({
				variables: {
					input: {
						automix: automixChannelId,
						source: soloChannelId
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					const mutationError = result.error ?? result.data?.updateAutomixSourceSelector.error;
					if (mutationError) {
						this.logger.error('setAutomixSoloMode', 'error', mutationError);
						throw new Error(mutationError.message);
					}

					this.logger.trace('setAutomixSoloMode', 'Done', { automixChannelId, soloChannelId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('setAutomixSoloMode', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public setPostGateSoloMode(channelId: string, isEnabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setPostGateSoloMode()', 'Setting post gate solo mode', { channelId, isEnabled });
		return this.updateChannel(channelId, { postGateSolo: { enabled: isEnabled } });
	}

	public setAutomixGainMeterEnabled(deviceId: string, status: boolean): Observable<Response<void, string>> {
		this.logger.trace('setAutomixGainMeterEnabled()', 'Toggling automix gain meter enabled', { deviceId, status });
		return this.intellimixDeviceAutomixGainMeteringMutationGQL
			.mutate({
				variables: {
					updates: [
						{
							device: {
								id: deviceId,
								features: {
									automixGainMetering: {
										enabled: status
									}
								}
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('setAutomixGainMeterEnabled()', 'Failed to toggle automix gain meter', {
							deviceId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('setAutomixGainMeterEnabled()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('setAutomixGainMeterEnabled()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public updateDeviceOperationMode(
		deviceId: string,
		mode: DeviceConferenceOperationMode
	): Observable<Response<void, string>> {
		this.logger.trace('updateDeviceOperationMode()', 'updating operation mode', { deviceId });
		return this.intellimixDeviceAutomixGainMeteringMutation
			.mutate({
				variables: {
					updates: [
						{
							device: {
								id: deviceId,
								features: {
									conferencingOperationMode: { mode }
								}
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateDeviceOperationMode()', 'Failed to set operation mode', {
							deviceId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateDeviceOperationMode()', 'Done', { deviceId, mode });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('updateDeviceOperationMode()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public resetAutomixerDefaults(channelId: string): Observable<Response<void, string>> {
		this.logger.trace('resetAutomixerDefaults()', 'resetting automixer defaults', { channelId: channelId });
		return this.resetAutomixerDefaultsMutationGQL
			.mutate({
				variables: {
					channelId: channelId,
					scope: DeviceResetScope.Automixer,
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					const resetError =
						result.error ??
						(result.data?.resetDevice && 'error' in result.data.resetDevice
							? result.data.resetDevice.error
							: null);
					if (resetError) {
						this.logger.error('resetAutomixerDefaults()', 'Failed to reset automixer defaults', {
							channelId: channelId,
							result: result,
							resetError
						});
						return { state: ResponseState.Error, error: resetError.message };
					}

					this.logger.trace('resetAutomixerDefaults()', 'reset automixer defaults successful', {
						channelId: channelId
					});
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('resetAutomixerDefaults()', 'Error', { error: error, channelId: channelId });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public setVirtualAcousticBoundaryEnabled(deviceId: string, isEnabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('setVirtualAcousticBoundaryEnabled()', 'Toggling virtual acoustic boundary enabled', {
			deviceId,
			isEnabled
		});
		return this.updateDevice(deviceId, { virtualAcousticBoundary: { enabled: isEnabled } });
	}

	public setVirtualAcousticBoundarySensitivity(
		deviceId: string,
		sensitivity: number
	): Observable<Response<void, string>> {
		this.logger.trace('setVirtualAcousticBoundarySensitivity()', 'Setting virtual acoustic boundary sensitivity', {
			deviceId,
			sensitivity
		});
		return this.updateDevice(deviceId, { virtualAcousticBoundary: { sensitivity: sensitivity } });
	}

	public setMaxOpenChannels(channelId: string, numChannels: number): Observable<Response<void, string>> {
		this.logger.trace('setMaxOpenChannels()', 'Setting max open channels', { channelId, numChannels });
		return this.updateChannel(channelId, { automixer: { maxOpenChannels: numChannels } });
	}

	public setMicOptimizationMode(
		channelId: string,
		mode: AutomixMicOptimizationMode
	): Observable<Response<void, string>> {
		this.logger.trace('setMicOptimizationMode()', 'Setting mic optimization mode', { channelId, mode });
		return this.updateChannel(channelId, { micOptimization: { mode: mapMicOptimizationModeToSysApi(mode) } });
	}

	public setKeepLastChannelActive(channelId: string, status: boolean): Observable<Response<void, string>> {
		this.logger.trace('setKeepLastChannelActive()', 'Toggling keep last channel active', { channelId, status });
		return this.updateChannel(channelId, { automixer: { leaveLastChannelOn: status } });
	}

	public setOffAttenuation(channelId: string, decibels: number): Observable<Response<void, string>> {
		this.logger.trace('setOffAttenuation()', 'Setting off attenuation', { channelId, decibels });
		return this.updateChannel(channelId, { automixer: { offAttenuation: decibels } });
	}

	public setHoldTime(channelId: string, holdTime: number): Observable<Response<void, string>> {
		this.logger.trace('setHoldTime()', 'Setting hold time', { channelId, holdTime });
		return this.updateChannel(channelId, { automixer: { holdTime: Duration.fromMillis(holdTime).toISO() } });
	}

	public setGatingSensitivity(channelId: string, sensitivity: number): Observable<Response<void, string>> {
		this.logger.trace('setGatingSensitivity()', 'Setting gating sensitivity', { channelId, sensitivity });
		return this.updateChannel(channelId, { automixer: { gatingSensitivity: sensitivity } });
	}

	public setDirectOutputTapPoint(
		channelId: string,
		tapPoint: DirectOutputTapPoint
	): Observable<Response<void, string>> {
		this.logger.trace('setDirectOutputTapPoint()', 'Setting direct output tap point', { channelId, tapPoint });
		return this.updateChannel(channelId, { directOutputTapPoint: { tapPoint: tapPoint } });
	}

	private updateChannel(
		channelId: string,
		update: AudioChannelFeatureUpdateInput
	): Observable<Response<void, string>> {
		return this.intellimixChannelMutationGQL
			.mutate({
				variables: {
					updates: [{ audioChannel: { id: channelId, features: update } }],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('updateChannel()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	private updateDevice(deviceId: string, update: DeviceFeatureUpdateInput): Observable<Response<void, string>> {
		return this.intellimixDeviceMutationGQL
			.mutate({
				variables: {
					updates: [{ device: { id: deviceId, features: update } }],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				errorPolicy: 'all'
			})
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateDevice()', 'Failed to update device', {
							deviceId,
							result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateDevice()', 'Done', { deviceId });
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('updateDevice()', 'Error', { error });
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}
}
